/**
 * Escalas de qualidade do ar — funções puras (testadas em `ar.test.ts`).
 *
 * **Cada rede aparece na régua dela** (mudança de 23/09/2026, a pedido do
 * usuário: pôr todos numa régua só mudava o dado e tirava a credibilidade). O
 * valor é o que a rede publica ou mostra no mapa dela, na unidade dela, e a
 * faixa e a cor são as da escala que ela usa — conferidas no código dos
 * próprios sites em 23/09/2026:
 *
 * - **Rede do Acre (UFAC/MPAC)**: MP2,5 corrigido pela rede (LRAPA), µg/m³;
 *   faixas próprias com limite superior exclusivo (< 12, < 35, < 55, < 150,
 *   < 250), cores do mapa dela (`_a`/`hk` em acrequalidadedoar.ufac.br).
 * - **RedeAr (IPAM/UFPA)**: média dos canais A e B, sem correção, µg/m³;
 *   faixas Boa até 15, Moderada até 50, Ruim até 75, Muito ruim até 125,
 *   Péssima acima (`PS` em redear.org.br). O mapa dela pinta num degradê
 *   entre as cores das faixas; aqui cada faixa tem a cor da própria parada.
 * - **PurpleAir**: o AQI de MP2,5 da EPA (tabela de 2024) sobre a média de
 *   10 min, sem conversão — o padrão do map.purpleair.com (`US_EPA_AQI`, a
 *   concentração truncada a 0,1 antes do índice), com as cores dele. O mapa
 *   dela também usa degradê; aqui, a cor de cada faixa.
 * - **AirGradient**: o PM2,5 que o mapa dela mostra (corrigido por ela),
 *   µg/m³, com as faixas de concentração da EPA de 2024 e as cores dela.
 * - **MonitorAr (MMA)**: o IQAr e a classificação oficial (CONAMA 491), com
 *   as cores do monitorar.mma.gov.br.
 * - **CONAMA 491 em concentração**: para quem não tem escala própria — a
 *   OpenAQ (que publica a medição sem classificar) e o modelo CAMS. É a
 *   tabela oficial brasileira para o MP2,5 em 24 h (0–25, 25–50, 50–75,
 *   75–125, > 125 µg/m³), com as cores do MonitorAr.
 *
 * Referência comum, só como comparação na ficha: diretriz da OMS de 2021
 * para o MP2,5 — 15 µg/m³ em 24 h.
 */

export type EscalaId = 'ufac' | 'redear' | 'purpleair' | 'airgradient' | 'iqar' | 'conama';

export interface Categoria {
  nome: string;
  cor: string;
  /** Limite superior na unidade da escala; `null` na última. */
  ate: number | null;
  /** O mesmo limite em µg/m³ de MP2,5 (para as linhas do gráfico), quando a escala é um índice. */
  ateUgm3?: number | null;
}

export interface Escala {
  id: EscalaId;
  /** "Rede do Acre (UFAC/MPAC)". */
  nome: string;
  /** Unidade do número exibido. */
  unidade: 'µg/m³' | 'AQI' | 'IQAr';
  /** `true`: o limite pertence à faixa de baixo (≤); `false`: à de cima (<). */
  inclusivo: boolean;
  /** Casas decimais do número exibido. */
  casas: number;
  categorias: readonly Categoria[];
}

/** Tabela da EPA de 2024 para o AQI de MP2,5 (a do mapa da PurpleAir): µg/m³ → índice. */
const EPA_AQI = [0, 50, 100, 150, 200, 300, 500];
const EPA_CONC: readonly [number, number][] = [
  [0, 9],
  [9.1, 35.4],
  [35.5, 55.4],
  [55.5, 125.4],
  [125.5, 225.4],
  [225.5, 325.4],
];

const COR_MONITORAR = ['#88CE40', '#FFD156', '#FFAA71', '#FF0000', '#990066'];
const NOMES_CONAMA = ['Boa', 'Moderada', 'Ruim', 'Muito ruim', 'Péssima'];

export const ESCALAS: Readonly<Record<EscalaId, Escala>> = {
  ufac: {
    id: 'ufac',
    nome: 'Rede do Acre (UFAC/MPAC)',
    unidade: 'µg/m³',
    inclusivo: false,
    casas: 1,
    categorias: [
      { nome: 'Boa', cor: '#68e143', ate: 12 },
      { nome: 'Moderada', cor: '#ffff55', ate: 35 },
      { nome: 'Insalubre (sensíveis)', cor: '#ef8533', ate: 55 },
      { nome: 'Insalubre', cor: '#ea3324', ate: 150 },
      { nome: 'Muito insalubre', cor: '#8c1a4b', ate: 250 },
      { nome: 'Perigosa', cor: '#731425', ate: null },
    ],
  },
  redear: {
    id: 'redear',
    nome: 'RedeAr (IPAM/UFPA)',
    unidade: 'µg/m³',
    inclusivo: true,
    casas: 1,
    categorias: [
      { nome: 'Boa', cor: '#529F2B', ate: 15 },
      { nome: 'Moderada', cor: '#F7D400', ate: 50 },
      { nome: 'Ruim', cor: '#E48B27', ate: 75 },
      { nome: 'Muito ruim', cor: '#CB0912', ate: 125 },
      { nome: 'Péssima', cor: '#8B0000', ate: null },
    ],
  },
  purpleair: {
    id: 'purpleair',
    nome: 'PurpleAir (AQI da EPA, 2024)',
    unidade: 'AQI',
    inclusivo: true,
    casas: 0,
    categorias: [
      { nome: 'Boa', cor: '#68E143', ate: 50, ateUgm3: 9 },
      { nome: 'Moderada', cor: '#FFFF00', ate: 100, ateUgm3: 35.4 },
      { nome: 'Insalubre (sensíveis)', cor: '#FF7E00', ate: 150, ateUgm3: 55.4 },
      { nome: 'Insalubre', cor: '#FF0000', ate: 200, ateUgm3: 125.4 },
      { nome: 'Muito insalubre', cor: '#8F3F97', ate: 300, ateUgm3: 225.4 },
      { nome: 'Perigosa', cor: '#7E0023', ate: null, ateUgm3: null },
    ],
  },
  airgradient: {
    id: 'airgradient',
    nome: 'AirGradient (faixas da EPA, 2024)',
    unidade: 'µg/m³',
    inclusivo: true,
    casas: 1,
    categorias: [
      { nome: 'Boa', cor: '#58D32F', ate: 9 },
      { nome: 'Moderada', cor: '#FFDA3E', ate: 35.4 },
      { nome: 'Insalubre (sensíveis)', cor: '#FF9300', ate: 55.4 },
      { nome: 'Insalubre', cor: '#E1243B', ate: 125.4 },
      { nome: 'Muito insalubre', cor: '#7C2DC1', ate: 225.4 },
      { nome: 'Perigosa', cor: '#822B22', ate: null },
    ],
  },
  iqar: {
    id: 'iqar',
    nome: 'IQAr oficial (MonitorAr, CONAMA 491)',
    unidade: 'IQAr',
    inclusivo: true,
    casas: 0,
    categorias: [40, 80, 120, 200, null].map((ate, k) => ({ nome: NOMES_CONAMA[k]!, cor: COR_MONITORAR[k]!, ate })),
  },
  conama: {
    id: 'conama',
    nome: 'CONAMA 491 (MP2,5 em 24 h)',
    unidade: 'µg/m³',
    inclusivo: true,
    casas: 1,
    categorias: [25, 50, 75, 125, null].map((ate, k) => ({ nome: NOMES_CONAMA[k]!, cor: COR_MONITORAR[k]!, ate })),
  },
};

/** Ordem em que as escalas aparecem na legenda. */
export const ORDEM_ESCALAS: readonly EscalaId[] = ['ufac', 'redear', 'purpleair', 'airgradient', 'iqar', 'conama'];

/** A faixa de um valor: a escala e o índice da categoria (`"redear:1"`). Comparável com `===`. */
export type Faixa = `${EscalaId}:${number}`;

/** Contorno de quem não tem dado no quadro. */
export const COR_SEM_DADO = '#8c99ab';

/** Diretriz da OMS (2021) para o MP2,5 em 24 h, µg/m³. */
export const OMS_PM25_24H = 15;

function numero(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * AQI de MP2,5 da EPA (2024) como o mapa da PurpleAir calcula: a concentração
 * truncada a 0,1, interpolada na faixa que a contém e arredondada. Acima de
 * 325,4 µg/m³ segue a reta da última faixa. `null` sem número ou negativo.
 */
export function aqiEpa(ugm3: number | null | undefined): number | null {
  if (!numero(ugm3)) return null;
  const c = Math.floor(ugm3 * 10) / 10;
  for (let r = EPA_CONC.length - 1; r > 0; r--) {
    const [lo, hi] = EPA_CONC[r]!;
    if (c >= lo) return Math.round(((EPA_AQI[r + 1]! - (EPA_AQI[r]! + 1)) / (hi - lo)) * (c - lo) + EPA_AQI[r]! + 1);
  }
  const [lo, hi] = EPA_CONC[0]!;
  return c >= lo ? Math.round(((EPA_AQI[1]! - EPA_AQI[0]!) / (hi - lo)) * (c - lo) + EPA_AQI[0]!) : null;
}

/**
 * O número na unidade da escala a partir do valor publicado: o AQI na da
 * PurpleAir (que publica a concentração e mostra o índice); nas outras, o
 * próprio valor.
 */
export function valorNaEscala(escala: EscalaId, valor: number | null | undefined): number | null {
  if (!numero(valor)) return null;
  return escala === 'purpleair' ? aqiEpa(valor) : valor;
}

/** Índice da categoria de um número já na unidade da escala. */
function categoria(e: Escala, v: number): number {
  const n = e.categorias.length;
  for (let k = 0; k < n - 1; k++) {
    const ate = e.categorias[k]!.ate!;
    if (e.inclusivo ? v <= ate : v < ate) return k;
  }
  return n - 1;
}

/**
 * Faixa do valor publicado na escala da rede. `null` sem número — nunca a
 * primeira faixa por falta de dado. Negativo cai na primeira, como zero.
 */
export function faixaNaEscala(escala: EscalaId, valor: number | null | undefined): Faixa | null {
  const v = valorNaEscala(escala, valor);
  if (v === null) return null;
  return `${escala}:${categoria(ESCALAS[escala], v)}`;
}

export function escalaDaFaixa(f: Faixa): Escala {
  return ESCALAS[f.slice(0, f.indexOf(':')) as EscalaId];
}

function categoriaDaFaixa(f: Faixa): Categoria {
  const e = escalaDaFaixa(f);
  return e.categorias[Number(f.slice(f.indexOf(':') + 1))] ?? e.categorias[0]!;
}

export function corDaFaixa(f: Faixa): string {
  return categoriaDaFaixa(f).cor;
}

export function nomeDaFaixa(f: Faixa): string {
  return categoriaDaFaixa(f).nome;
}

/** Todas as faixas de uma escala, na ordem. */
export function faixasDa(escala: EscalaId): Faixa[] {
  return ESCALAS[escala].categorias.map((_, k) => `${escala}:${k}` as Faixa);
}

/** Faixa de uma concentração de MP2,5 pela tabela do CONAMA 491 (OpenAQ e modelo CAMS). */
export function faixaPm25(ugm3: number | null | undefined): Faixa | null {
  return faixaNaEscala('conama', ugm3);
}

/** Faixa de um IQAr (índice adimensional) pela tabela do CONAMA 491. */
export function faixaIqar(indice: number | null | undefined): Faixa | null {
  return faixaNaEscala('iqar', indice);
}

/**
 * Classificação que o MonitorAr publica (`classificacaoIqAr.id`): 1 Boa,
 * 2 Moderada, 3 Ruim, 4 Muito Ruim, 5 Péssima. Id desconhecido: `null`
 * (quem chama cai no índice).
 */
export function faixaDoMonitorAr(id: number | null | undefined): Faixa | null {
  return typeof id === 'number' && Number.isInteger(id) && id >= 1 && id <= 5 ? `iqar:${id - 1}` : null;
}

/** Faixa oficial se houver; senão a do índice. */
export function faixaDaEstacao(iqar: number | null, idClasse: number | null): Faixa | null {
  return faixaDoMonitorAr(idClasse) ?? faixaIqar(iqar);
}

function fmt(v: number): string {
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

/** Texto do intervalo de uma faixa na unidade da escala, para a legenda ("até 12", "12–35", "250 ou mais"). */
export function intervaloDaFaixa(f: Faixa): string {
  const e = escalaDaFaixa(f);
  const k = Number(f.slice(f.indexOf(':') + 1));
  const sup = e.categorias[k]?.ate ?? null;
  const inf = k === 0 ? null : (e.categorias[k - 1]?.ate ?? null);
  if (inf === null && sup !== null) return e.inclusivo ? `até ${fmt(sup)}` : `abaixo de ${fmt(sup)}`;
  if (sup === null) return inf === null ? '' : e.inclusivo ? `acima de ${fmt(inf)}` : `${fmt(inf)} ou mais`;
  return `${fmt(inf!)}–${fmt(sup)}`;
}

/** Linhas de referência do gráfico em µg/m³: o limite superior de cada faixa, na cor da faixa de cima. */
export function referenciasUgm3(escala: EscalaId): { valor: number; cor: string }[] {
  const cats = ESCALAS[escala].categorias;
  const out: { valor: number; cor: string }[] = [];
  for (let k = 0; k < cats.length - 1; k++) {
    const c = cats[k]!;
    const v = c.ateUgm3 !== undefined ? c.ateUgm3 : c.ate;
    if (v !== null && v !== undefined) out.push({ valor: v, cor: cats[k + 1]!.cor });
  }
  return out;
}

/** Linhas de referência na unidade da própria escala (o IQAr das estações). */
export function referenciasDaEscala(escala: EscalaId): { valor: number; cor: string }[] {
  const cats = ESCALAS[escala].categorias;
  return cats.slice(0, -1).map((c, k) => ({ valor: c.ate!, cor: cats[k + 1]!.cor }));
}

/** "12,6" — uma casa decimal, vírgula. */
export function pmTexto(ugm3: number | null | undefined, casas = 1): string | null {
  if (typeof ugm3 !== 'number' || !Number.isFinite(ugm3)) return null;
  return ugm3.toFixed(casas).replace('.', ',');
}

/**
 * Média das medições de uma janela, só com cobertura suficiente — o critério
 * de representatividade usual das redes oficiais é 75% da janela. Abaixo
 * disso, `null`: uma "média de 24 h" de três leituras não é média de 24 h.
 */
export function mediaComCobertura(valores: readonly (number | null)[], coberturaMinima = 0.75): number | null {
  if (valores.length === 0) return null;
  let soma = 0;
  let n = 0;
  for (const v of valores) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      soma += v;
      n++;
    }
  }
  if (n === 0 || n / valores.length < coberturaMinima) return null;
  return soma / n;
}
